import { useGetAgendasLookupsQuery, useGetTableLookupsQuery } from 'store/services/flow/lookups';
import { useGetAgendasFormsQuery } from 'store/services/flow/form';
import { useGetAdminViewQuery } from 'store/services/flow/views';
import { memo, useCallback, useEffect, useMemo } from 'react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Delete, DragIndicator } from '@mui/icons-material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { EMAIL_REGEX } from 'utils/regex';
import { Box, FormControlLabel, MenuItem, styled } from '@mui/material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Switch } from 'components/mui/common/Switch';
import { Checkbox } from 'components/mui/common/Checkbox';
import { IconButton } from 'components/mui/common/IconButton';
import { Select } from 'components/mui/common/Select';
import { TextField } from 'components/mui/common/TextField';
import { Button } from 'components/mui/common/Button';
import { VIEW_ITEM_STATE } from 'types/enums';
import type { ColumnType, FormItemType, ViewCondition } from '../../types';

const recordsDisplayFormItemTypes = new Set<string>([
  'Text',
  'Number',
  'Email',
  'PhoneNumber',
  'AutomaticSequence',
  'RichText',
  'Computed',
  'RadioButton',
  'Date',
] satisfies FormItemType[]);

// Kept in sync with ConditionsRow so a comparison that takes no value never renders an enabled value field.
const comparisonsTypeWhichDoesNotNeedValue = ['IsEmpty', 'IsNotEmpty', 'None', 'IsPressed', 'IsChecked', 'IsUnchecked'];

const ConditionsWrapper = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '8px',
  paddingLeft: '60px',
}));

const RowWrapper = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
}));

const OPERATORS: ViewCondition['logicalOperator'][] = ['And', 'Or'];

const Wrapper = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
}));

interface Props {
  index: number;
  handleRemoveColumn: (id: string) => void;
  parentType: string;
  columnType: ColumnType['type'] | FormItemType;
  editable?: boolean;
  agendaId: string;
  id: string;
}

export const ViewControlRow = memo(({ index, handleRemoveColumn, parentType, columnType, editable = false, id, agendaId }: Props) => {
  const { t } = useTranslation();
  const registerName = `shownColumns[${index}]`;

  const { control, setValue } = useFormContext();

  const [
    columnId,
    name,
    type,
    comparisonType,
    isVariable,
    isEnable,
    isFiltering,
    displayInFilter,
    isEditable,
    showInToolbar,
    showOnlyExistingValues,
  ] = useWatch({
    control,
    name: [
      `${registerName}.columnId`,
      `${registerName}.name`,
      `${registerName}.type`,
      `${registerName}.comparisonType`,
      `${registerName}.isVariable`,
      `${registerName}.isEnable`,
      `${registerName}.isFiltering`,
      `${registerName}.displayInFilter`,
      `${registerName}.isEditable`,
      `${registerName}.showInToolbar`,
      `${registerName}.showOnlyExistingValues`,
    ],
  });

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: columnId,
  });

  const conditions: ViewCondition[] = useWatch({ control, name: `${registerName}.conditions` }) ?? [];

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const { data: { comparisons: agendaComparisons } = {} } = useGetAgendasLookupsQuery(
    {
      formItemId: columnId,
    },
    { skip: parentType !== 'Agenda' }
  );

  const { data: { comparisons: tableComparisons } = {} } = useGetTableLookupsQuery(
    {
      columnId: columnId,
    },
    { skip: parentType !== 'Table' }
  );

  const comparisonTypes = useMemo(() => {
    const types = agendaComparisons || tableComparisons;

    if (types && types.length > 0) {
      const filteredTypes = types.filter((value) => value !== 'IsBiggerBy' && value !== 'IsSmallerBy');
      return ['None', ...filteredTypes];
    }
    return ['None'];
  }, [agendaComparisons, tableComparisons]);

  useEffect(() => {
    if (isVariable) {
      setValue(`${registerName}.staticValue`, '');
    }
  }, [isVariable, registerName, setValue]);

  const getIsComparisonType = useCallback(
    (valueType?: string) => comparisonsTypeWhichDoesNotNeedValue.includes(valueType || comparisonType),
    [comparisonType]
  );

  const getComponentByDataType = useMemo(() => {
    const commonProps = {
      label: name,
      fullWidth: true,
      background: 'white' as const,
      placeholder: `${t('common.write')} ${name}`,
      disabled: isVariable || getIsComparisonType(),
    };

    const renderTextField = (fieldType: string, rules = {}) => (
      <Controller
        control={control}
        name={`${registerName}.staticValue`}
        rules={rules}
        render={({ field, fieldState: { error } }) => (
          <TextField {...field} {...commonProps} type={fieldType} error={Boolean(error)} helperText={error?.message} />
        )}
      />
    );

    const typeConfig: Record<string, () => JSX.Element> = {
      Number: () => renderTextField('number'),
      Email: () =>
        renderTextField('email', {
          pattern: {
            value: EMAIL_REGEX,
            message: t('common.invalidEmail'),
          },
        }),
      Text: () => renderTextField('text'),
    };

    return (typeConfig[type as keyof typeof typeConfig] || typeConfig.Text)();
  }, [control, getIsComparisonType, isVariable, name, registerName, t, type]);

  const renderConditionValueField = useCallback(
    (fieldName: string, disabled: boolean) => {
      const commonProps = {
        label: name,
        fullWidth: true,
        background: 'white' as const,
        placeholder: `${t('common.write')} ${name}`,
        disabled,
      };

      const renderTextField = (fieldType: string, rules = {}) => (
        <Controller
          control={control}
          name={fieldName}
          rules={rules}
          render={({ field, fieldState: { error } }) => (
            <TextField {...field} {...commonProps} type={fieldType} error={Boolean(error)} helperText={error?.message} />
          )}
        />
      );

      const typeConfig: Record<string, () => JSX.Element> = {
        Number: () => renderTextField('number'),
        Email: () =>
          renderTextField('email', {
            pattern: {
              value: EMAIL_REGEX,
              message: t('common.invalidEmail'),
            },
          }),
        Text: () => renderTextField('text'),
      };

      return (typeConfig[type as keyof typeof typeConfig] || typeConfig.Text)();
    },
    [control, name, t, type]
  );

  const isEditableDisabled = columnType === 'Computed';

  const isRecordsColumn = parentType === 'Agenda' && type === 'Records';

  const { currentData: agendaForm, isFetching: isFetchingAgendaForm } = useGetAgendasFormsQuery(
    { agendaId },
    { skip: !isRecordsColumn || !agendaId }
  );

  const recordsDefinition = useMemo(() => {
    for (const tabSection of agendaForm?.tabSections ?? []) {
      for (const section of tabSection.sections ?? []) {
        const sectionItem = section.items?.find(({ id: itemId }) => itemId === id);

        if (sectionItem) return sectionItem.definition?.records ?? sectionItem.definition?.Records;

        for (const tab of section.formItemTabs ?? []) {
          const tabItem = tab.items?.find(({ id: itemId }) => itemId === id);

          if (tabItem) return tabItem.definition?.records ?? tabItem.definition?.Records;
        }
      }
    }

    return undefined;
  }, [agendaForm, id]);

  const targetAgendaId = recordsDefinition?.agendaId ?? '';
  const targetViewId = recordsDefinition?.viewId ?? '';

  const { currentData: targetView, isFetching: isFetchingTargetView } = useGetAdminViewQuery(
    {
      parentId: targetAgendaId,
      viewId: targetViewId,
      viewParentType: 'Agenda',
    },
    { skip: !isRecordsColumn || !targetAgendaId || !targetViewId }
  );

  const viewFormItems = useMemo(
    () =>
      (targetView?.viewParameters ?? [])
        .filter(
          ({ viewItemState, formItemType }) =>
            (viewItemState === VIEW_ITEM_STATE.ENABLED_AND_FILTER || viewItemState === VIEW_ITEM_STATE.ENABLED_AND_NO_FILTER) &&
            recordsDisplayFormItemTypes.has(formItemType ?? '')
        )
        .map(({ parameterId, parameterName }) => ({
          id: parameterId,
          name: parameterName,
        })),
    [targetView]
  );

  return (
    <RowWrapper>
      <Wrapper ref={setNodeRef} style={style} data-testid={`admin-flow-table-view-row-${id}`}>
        <IconButton {...attributes} {...listeners}>
          <DragIndicator />
        </IconButton>
        <Controller
          control={control}
          name={`${registerName}.isEnable`}
          defaultValue={isEnable}
          render={({ field }) => (
            <Switch
              {...field}
              onChange={(_, value) => field.onChange(value)}
              checked={isEnable}
              dataTestId={`admin-flow-table-view-row-${id}-enabled-switch`}
            />
          )}
        />
        <Box width="150px" mb={-1}>
          <Select
            value={comparisonType}
            background="white"
            displayEmpty
            fullWidth
            dataTestId={`admin-flow-table-view-row-${id}-comparison-select`}
            disabled={comparisonTypes.length === 1 /* Only None */}
            onChange={(e) => {
              const value = e.target.value;
              setValue(`${registerName}.comparisonType`, value, { shouldDirty: true });
              setValue(`${registerName}.staticValue`, '', { shouldDirty: true });
              if (value === 'None') {
                setValue(`${registerName}.conditions`, [], { shouldDirty: true });
              }
              if (getIsComparisonType(value as string)) {
                setValue(`${registerName}.isVariable`, false, { shouldDirty: true });
              }
            }}>
            {(
              comparisonTypes?.map((item: string) => ({
                id: item,
                value: item,
                label: t(`view.table.${item}`),
              })) ?? [
                {
                  id: 'None',
                  value: 'None',
                  label: t('view.table.None'),
                },
              ]
            ).map((column) => (
              <MenuItem key={column.id} value={column.value}>
                {column.label}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Box width="300px" mb={2}>
          {getComponentByDataType}
        </Box>
        {parentType === 'Agenda' && (
          <>
            <FormControlLabel
              label={t('view.table.filtering')}
              control={
                <Controller
                  control={control}
                  name={`${registerName}.isFiltering`}
                  disabled={comparisonTypes.length === 1 /* Only None */}
                  render={({ field }) => (
                    <Checkbox {...field} checked={isFiltering} dataTestId={`admin-flow-table-view-row-${id}-filtering-checkbox`} />
                  )}
                />
              }
            />
            <FormControlLabel
              label={t('flow.view.displayInParentWhenFiltering')}
              control={
                <Controller
                  control={control}
                  name={`${registerName}.displayInFilter`}
                  render={({ field }) => (
                    <Checkbox {...field} checked={displayInFilter} dataTestId={`admin-flow-table-view-row-${id}-display-in-filter-checkbox`} />
                  )}
                />
              }
            />
          </>
        )}
        <FormControlLabel
          label={t('view.table.variable')}
          control={
            <Controller
              control={control}
              name={`${registerName}.isVariable`}
              disabled={getIsComparisonType()}
              render={({ field }) => <Checkbox {...field} checked={isVariable} dataTestId={`admin-flow-table-view-row-${id}-variable-checkbox`} />}
            />
          }
        />
        {type === 'Tables' && isFiltering && (
          <FormControlLabel
            label={t('flow.view.showOnlyExistingValues')}
            control={
              <Controller
                control={control}
                name={`${registerName}.showOnlyExistingValues`}
                disabled={!isFiltering}
                render={({ field }) => (
                  <Checkbox
                    {...field}
                    checked={showOnlyExistingValues}
                    dataTestId={`admin-flow-table-view-row-${id}-show-only-existing-values-checkbox`}
                  />
                )}
              />
            }
          />
        )}
        {parentType === 'Agenda' && type === 'Records' && (
          <Box width="150px">
            <Controller
              name={`${registerName}.attributeFormItemId`}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Select
                  {...field}
                  value={field.value ?? ''}
                  onChange={(event) => field.onChange(event.target.value || null)}
                  disabled={isFetchingAgendaForm || isFetchingTargetView || !targetAgendaId || !targetViewId}
                  label={t('flow.view.recordsDisplayColumn')}
                  fullWidth
                  error={Boolean(error)}
                  helperText={error?.message}
                  background="white"
                  displayEmpty
                  dataTestId={`admin-flow-table-view-row-${id}-attribute-select`}>
                  {viewFormItems.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </Box>
        )}
        {editable && (
          <FormControlLabel
            label={t('view.table.editable')}
            control={
              <Controller
                control={control}
                name={`${registerName}.isEditable`}
                render={({ field }) => (
                  <Checkbox
                    {...field}
                    checked={isEditable}
                    disabled={isEditableDisabled}
                    dataTestId={`admin-flow-table-view-row-${id}-editable-checkbox`}
                  />
                )}
              />
            }
          />
        )}
        {columnType === 'Button' && (
          <FormControlLabel
            label={t('flow.view.showInToolbar')}
            control={
              <Controller
                control={control}
                name={`${registerName}.showInToolbar`}
                render={({ field }) => (
                  <Checkbox {...field} checked={showInToolbar} dataTestId={`admin-flow-table-view-row-${id}-show-in-toolbar-checkbox`} />
                )}
              />
            }
          />
        )}
        <IconButton onClick={() => handleRemoveColumn(columnId)} data-testid={`admin-flow-table-view-row-${id}-delete-button`}>
          <Delete />
        </IconButton>
      </Wrapper>
      {comparisonType && comparisonType !== 'None' && (
        <ConditionsWrapper>
          {conditions.map((condition, conditionIndex) => {
            const conditionName = `${registerName}.conditions.${conditionIndex}`;

            return (
              <Wrapper key={conditionName}>
                <Box width="100px">
                  <Select
                    value={condition.logicalOperator}
                    background="white"
                    fullWidth
                    dataTestId={`admin-flow-table-view-row-${id}-condition-${conditionIndex}-operator-select`}
                    onChange={(e) => setValue(`${conditionName}.logicalOperator`, e.target.value, { shouldDirty: true })}>
                    {OPERATORS.map((operator) => (
                      <MenuItem key={operator} value={operator}>
                        {t(`common.${operator.toLowerCase()}`)}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
                <Box width="150px">
                  <Select
                    value={condition.comparisonType}
                    background="white"
                    displayEmpty
                    fullWidth
                    dataTestId={`admin-flow-table-view-row-${id}-condition-${conditionIndex}-comparison-select`}
                    onChange={(e) => {
                      setValue(`${conditionName}.comparisonType`, e.target.value, { shouldDirty: true });
                      setValue(`${conditionName}.staticValue`, '', { shouldDirty: true });
                    }}>
                    {comparisonTypes
                      .filter((item) => item !== 'None')
                      .map((item) => (
                        <MenuItem key={item} value={item}>
                          {t(`view.table.${item}`)}
                        </MenuItem>
                      ))}
                  </Select>
                </Box>
                <Box width="300px">{renderConditionValueField(`${conditionName}.staticValue`, getIsComparisonType(condition.comparisonType))}</Box>
                <IconButton
                  aria-label={t('common.delete')}
                  onClick={() =>
                    setValue(
                      `${registerName}.conditions`,
                      conditions.filter((_, indexToRemove) => indexToRemove !== conditionIndex),
                      { shouldDirty: true }
                    )
                  }
                  data-testid={`admin-flow-table-view-row-${id}-condition-${conditionIndex}-delete-button`}>
                  <Delete />
                </IconButton>
              </Wrapper>
            );
          })}
          <Button
            variant="text"
            onClick={() =>
              setValue(`${registerName}.conditions`, [...conditions, { comparisonType, staticValue: '', logicalOperator: 'And' }], {
                shouldDirty: true,
              })
            }
            data-testid={`admin-flow-table-view-row-${id}-add-condition-button`}>
            {t('view.table.addCondition')}
          </Button>
        </ConditionsWrapper>
      )}
    </RowWrapper>
  );
});
